/**
 * Ni² Scope Context — one shared source-of-truth for the retrieval
 * scope selected by the surgeon. Backs a compact selector that
 * appears next to every Ni² composer.
 *
 * Options: personal | department | literature | synthesize
 * Persistence: sessionStorage under the key `ni2_scope` so the scope
 * survives navigation between Ni² surfaces within the same tab but
 * resets to `personal` on a fresh browser session — matching the
 * "personal surgical memory first" default.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const NI2_SCOPE_OPTIONS = [
  { value: "personal",   label: "My Memory",     displayLabel: "My Memory" },
  { value: "department", label: "Department",    displayLabel: "Department Memory" },
  { value: "literature", label: "Literature",    displayLabel: "Literature" },
  { value: "synthesize", label: "Synthesize All", displayLabel: "Synthesis: Personal + Department + Literature" },
];

const STORAGE_KEY = "ni2_scope";
const DEFAULT_SCOPE = "personal";

const Ni2ScopeContext = createContext(null);

export function Ni2ScopeProvider({ children }) {
  const [scope, setScopeState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && NI2_SCOPE_OPTIONS.some((o) => o.value === stored)) return stored;
    } catch {}
    return DEFAULT_SCOPE;
  });

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, scope); } catch {}
  }, [scope]);

  const setScope = useCallback((next) => {
    if (!NI2_SCOPE_OPTIONS.some((o) => o.value === next)) return;
    setScopeState(next);
  }, []);

  const value = useMemo(() => {
    const opt = NI2_SCOPE_OPTIONS.find((o) => o.value === scope) || NI2_SCOPE_OPTIONS[0];
    return {
      scope,
      setScope,
      options: NI2_SCOPE_OPTIONS,
      label: opt.label,
      displayLabel: opt.displayLabel,
    };
  }, [scope, setScope]);

  return <Ni2ScopeContext.Provider value={value}>{children}</Ni2ScopeContext.Provider>;
}

export function useNi2Scope() {
  const ctx = useContext(Ni2ScopeContext);
  if (!ctx) {
    return {
      scope: DEFAULT_SCOPE,
      setScope: () => {},
      options: NI2_SCOPE_OPTIONS,
      label: NI2_SCOPE_OPTIONS[0].label,
      displayLabel: NI2_SCOPE_OPTIONS[0].displayLabel,
    };
  }
  return ctx;
}
